import { createSlice } from '@reduxjs/toolkit';

interface Experience {
    id: number;
    screens: string[];
    sharing?: Sharing[];
    processingFlows?: string[];
    exitPin?: string;
}

export interface ExperienceState {
    entities: Record<string, Experience>;
    ids: string[];
}

const initialState: ExperienceState = {
    entities: {},
    ids: [],
};

const experienceSlice = createSlice({
    name: 'experience',
    initialState,
    reducers: {
        addExperience(state, action) {
            const experience = action.payload;
            const id = experience.id;
            state.entities[id] = experience;

            if (!state.ids.includes(id)) {
                state.ids.push(id);
            }
        },
        removeExperience(state, action) {
            const { id } = action.payload;

            delete state.entities[id];
            state.ids = state.ids.filter((screenId) => screenId !== id);
        },
        resetExperience(state) {
            state.entities = {};
            state.ids = [];
        },
        addSharingStation(state, action) {
            const { experienceId, sharing } = action.payload;
            const experience = state.entities[experienceId];
            if (experience) {
                if (!experience.sharing) {
                    experience.sharing = [];
                }
                experience.sharing.push(sharing);
            }
        },
        removeSharingStation(state, action) {
            const { experienceId, sharingName } = action.payload;
            const experience = state.entities[experienceId];
            if (experience && experience.sharing) {
                experience.sharing = experience.sharing.filter(
                    (sharing: any) => sharing.name !== sharingName,
                );
            }
        },
        updateSharingStation(state, action) {
            const { experienceId, oldName, newSharing } = action.payload;
            const experience = state.entities[experienceId];
            if (experience && experience.sharing) {
                const index = experience.sharing.findIndex(
                    (sharing: any) => sharing.name === oldName,
                );
                if (index !== -1) {
                    experience.sharing[index] = newSharing;
                }
            }
        },
        addScreenToExperience(state, action) {
            const { experienceId, screenId } = action.payload;
            const experience = state.entities[experienceId];
            if (experience) {
                if (!experience.screens.includes(screenId)) {
                    experience.screens.push(screenId);
                    console.log(
                        `Added screen ${screenId} to experience ${experienceId}`,
                    );
                }
            }
        },
        removeScreenFromExperience(state, action) {
            const { experienceId, screenId } = action.payload;
            const experience = state.entities[experienceId];
            if (experience) {
                experience.screens = experience.screens.filter(
                    (id) => id !== screenId,
                );
                console.log(
                    `Removed screen ${screenId} from experience ${experienceId}`,
                );
            }
        },
        updateScreenIdInExperience(state, action) {
            const { experienceId, oldId, newId } = action.payload;
            const experience = state.entities[experienceId];
            if (experience) {
                // Update screen ID in the experience's screens array
                experience.screens = experience.screens.map((screenId) =>
                    screenId === oldId ? newId : screenId,
                );
                console.log(
                    `Updated screen ID in experience ${experienceId}: ${oldId} -> ${newId}`,
                );
            }
        },
    },
});

export const {
    addExperience,
    removeExperience,
    resetExperience,
    addSharingStation,
    removeSharingStation,
    updateSharingStation,
    addScreenToExperience,
    removeScreenFromExperience,
    updateScreenIdInExperience,
} = experienceSlice.actions;

export default experienceSlice.reducer;
